/**
 * EmbeddedEventEmitter — Efflux → Host (eventos)
 * ==============================================
 *
 * Emite eventos do player pro host. Enquanto o host ainda não sinalizou que está
 * pronto (`markReady()`), os eventos ficam num BUFFER e são drenados em ordem
 * assim que o host aparece — resolve R2.4 (evento perdido antes do listener do
 * host registrar, ex.: `ready` disparado antes do host injetar `EffluxHost`).
 *
 * Transporte é injetado (`send`) → 100% testável sem WebView/Android.
 */
import {
  EventEnvelope,
  EventPayloadMap,
  EventType,
  makeEvent,
} from './protocol';

/** Função de transporte: entrega o JSON do envelope ao host. */
export type EventTransport = (json: string) => void;

export class EmbeddedEventEmitter {
  private ready = false;
  private readonly buffer: EventEnvelope[] = [];

  /** Cap defensivo — se o host nunca ficar pronto, não crescer sem limite. */
  private static readonly MAX_BUFFER = 200;

  constructor(private readonly send: EventTransport) {}

  /**
   * Host sinalizou que está pronto pra receber. Drena o buffer em ordem e passa
   * a entregar ao vivo. Idempotente.
   */
  markReady(): void {
    if (this.ready) return;
    this.ready = true;
    const pending = this.buffer.splice(0, this.buffer.length);
    for (const env of pending) {
      this.deliver(env);
    }
  }

  isReady(): boolean {
    return this.ready;
  }

  /** Emite um evento tipado (monta o envelope). */
  emit<T extends EventType>(type: T, payload?: EventPayloadMap[T]): void {
    const env = makeEvent(type, payload);
    if (!this.ready) {
      if (this.buffer.length >= EmbeddedEventEmitter.MAX_BUFFER) {
        this.buffer.shift(); // descarta o mais antigo (ring)
      }
      this.buffer.push(env);
      return;
    }
    this.deliver(env);
  }

  /** Nº de eventos aguardando o host (útil pra teste/diagnóstico). */
  pendingCount(): number {
    return this.buffer.length;
  }

  private deliver(env: EventEnvelope): void {
    try {
      this.send(JSON.stringify(env));
    } catch (err) {
      // Nunca deixar um erro de transporte derrubar o player.
      console.debug('[EmbeddedEventEmitter] send falhou:', err);
    }
  }
}
